<template>
  <v-container id="technical-review-list">
    <app-city-header />

    <v-row class="mt-4" align="center">
      <v-col cols="12" md="4">
        <v-select
          v-model="filters.status"
          :items="statusOptions"
          item-text="text"
          item-value="value"
          clearable
          label="Filtrar por status"
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-text-field
          v-model="filters.search"
          label="Buscar por critério"
          clearable
        />
      </v-col>
      <v-col cols="12" md="4" class="text-right">
        <v-btn
          color="primary"
          class="ma-2"
          :to="newReviewRoute"
          v-if="canCreate"
        >
          Nova revisão técnica
        </v-btn>
      </v-col>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="filteredReviews"
      :loading="loading"
      loading-text="Carregando revisões técnicas..."
      class="elevation-1"
      :no-data-text="
        loading ? 'Carregando...' : 'Nenhuma revisão técnica cadastrada'
      "
      :footer-props="{ itemsPerPageOptions: [10, 25, 50] }"
    >
      <template v-slot:item.status="{ item }">
        <technical-review-status-chip :status="item.status" />
      </template>
      <template v-slot:item.dataAtualizacao="{ item }">
        {{ formatDate(item.dataAtualizacao || item.dataCriacao) }}
      </template>
      <template v-slot:item.acoes="{ item }">
        <v-btn
          text
          small
          color="primary"
          :to="detailRoute(item.seloRevisaoTecnicaId)"
        >
          Detalhes
        </v-btn>
        <v-btn
          text
          small
          color="secondary"
          :to="editRoute(item.seloRevisaoTecnicaId)"
          v-if="canEdit(item)"
        >
          Editar
        </v-btn>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import { AppCityHeader } from "@/modules/city";
import { technicalReviewAPI } from "../API/TechnicalReviewAPI";
import { extractCriterionDescription } from "@/helpers/criteria";
import { shouldTreatAsNational } from "@/utils/nationalEvaluation";
import {
  REVIEW_STATUSES,
  findStatusMeta,
  normalizeReviewStatus,
} from "../constants";
import TechnicalReviewStatusChip from "./StatusChip.vue";

export default {
  name: "TechnicalReviewList",
  components: { AppCityHeader, TechnicalReviewStatusChip },
  data() {
    return {
      loading: false,
      reviews: [],
      filters: {
        status: null,
        search: "",
      },
      headers: [
        { text: "Critério", value: "perguntaTitulo", sortable: true },
        { text: "Status", value: "status", sortable: true },
        {
          text: "Atualizado",
          value: "dataAtualizacao",
          sortable: true,
        },
        { text: "Solicitante", value: "responsavelNome", sortable: true },
        { text: "Ações", value: "acoes", sortable: false },
      ],
      config: JSON.parse(localStorage.getItem("config")) || {},
      groupsConfig: JSON.parse(localStorage.getItem("groupsConfig")) || {},
    };
  },
  computed: {
    ...mapGetters(["isFullAdmin", "cityId"]),
    municipioId() {
      return this.$route.params.municipioId || this.cityId;
    },
    statusOptions() {
      return REVIEW_STATUSES.filter((status) => {
        if (status.value === "GESTAO_NACIONAL") {
          return false;
        }
        if (!this.canViewDrafts && status.value === "RASCUNHO") {
          return false;
        }
        return true;
      });
    },
    hasReviewAccess() {
      return (
        this.isFullAdmin ||
        ((this.config.revisaoTecnica ?? false) &&
          (this.groupsConfig.revisaoTecnica ?? false))
      );
    },
    canManage() {
      return this.hasReviewAccess;
    },
    canViewDrafts() {
      if (this.isFullAdmin) {
        return true;
      }

      const configFlag = this.config.visualizarRascunhosRevisaoTecnica;
      const groupFlag = this.groupsConfig.visualizarRascunhosRevisaoTecnica;

      const configAllowed = configFlag === undefined ? true : Boolean(configFlag);
      const groupAllowed = groupFlag === undefined ? true : Boolean(groupFlag);

      return configAllowed && groupAllowed;
    },
    filteredReviews() {
      const search = (this.filters.search || "").toLowerCase();

      return this.reviews.filter((item) => {
        const matchesStatus =
          !this.filters.status || item.status === this.filters.status;

        const matchesSearch =
          !search ||
          (item.perguntaTitulo &&
            item.perguntaTitulo.toLowerCase().includes(search));

        const matchesDraftVisibility =
          this.canViewDrafts || item.status !== "RASCUNHO";

        const matchesTypePermission = this.hasReviewAccess
          ? true
          : this.canViewReview(item);

        return (
          matchesStatus &&
          matchesSearch &&
          matchesDraftVisibility &&
          matchesTypePermission
        );
      });
    },
    canCreate() {
      if (this.isFullAdmin) {
        return true;
      }

      const podeAcessarRevisoes =
        (this.config.revisaoTecnica ?? false) &&
        (this.groupsConfig.revisaoTecnica ?? false);

      const podeCriarRevisoes =
        (this.config.criarRevisaoTecnica ?? false) &&
        (this.groupsConfig.criarRevisaoTecnica ?? false);

      return podeAcessarRevisoes && podeCriarRevisoes;
    },
    newReviewRoute() {
      return `/cidade/${this.municipioId}/revisoes-tecnicas/nova`;
    },
  },
  created() {
    this.load();
  },
  watch: {
    "$route.params.municipioId"() {
      this.load();
    },
  },
  methods: {
    normalizeBooleanFlag(value, defaultValue = false) {
      if (value === undefined || value === null) {
        return defaultValue;
      }

      if (value === true || value === 1) {
        return true;
      }

      if (value === false || value === 0) {
        return false;
      }

      if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (["1", "true", "sim", "yes"].includes(normalized)) {
          return true;
        }
        if (["0", "false", "nao", "não", "no"].includes(normalized)) {
          return false;
        }
      }

      return Boolean(value);
    },
    normalizeClienteOcultoFlag(value) {
      return this.normalizeBooleanFlag(value, false);
    },
    canViewReview(item) {
      if (this.isFullAdmin) {
        return true;
      }

      const ehClienteOculto = this.normalizeClienteOcultoFlag(
        item?.ehPerguntaClienteOculto
      );
      const tratarComoNacional = shouldTreatAsNational(
        item?.perguntaId,
        ehClienteOculto
      );

      if (tratarComoNacional) {
        const groupNacional = this.normalizeBooleanFlag(
          this.groupsConfig?.avaliaRevisaoTecnicaNacional,
          true
        );
        const configNacional = this.normalizeBooleanFlag(
          this.config?.avaliaRevisaoTecnicaNacional,
          true
        );
        return groupNacional && configNacional;
      }

      const groupEstadual = this.normalizeBooleanFlag(
        this.groupsConfig?.avaliaRevisaoTecnicaEstadual,
        true
      );
      const configEstadual = this.normalizeBooleanFlag(
        this.config?.avaliaRevisaoTecnicaEstadual,
        true
      );
      return groupEstadual && configEstadual;
    },
    async load() {
      this.loading = true;
      try {
        const { data } = await technicalReviewAPI.list(this.municipioId);
        const revisoes =
          data.RevisoesTecnicas || data.revisoesTecnicas || data.recursos || [];
        this.reviews = revisoes.map((item) => this.normalize(item));
      } catch (error) {
        console.error("Erro ao carregar revisões técnicas", error);
        this.$toast?.error?.(
          "Não foi possível carregar as revisões técnicas"
        );
      } finally {
        this.loading = false;
      }
    },
    normalize(item) {
      const ehClienteOculto = this.normalizeClienteOcultoFlag(
        item.EhPerguntaClienteOculto ?? item.ehPerguntaClienteOculto ?? false
      );
      const ehRevisaoNacional = shouldTreatAsNational(
        item.PerguntaId ?? item.perguntaId ?? null,
        ehClienteOculto
      );
      const perguntaId = item.PerguntaId ?? item.perguntaId ?? null;
      const perguntaTituloBase =
        item.PerguntaTitulo ??
        item.perguntaTitulo ??
        item.CriterioTitulo ??
        item.criterioTitulo ??
        item.ClienteOcultoPerguntaTitulo ??
        item.clienteOcultoPerguntaTitulo ??
        "";
      const grupoNomeBase =
        item.GrupoNome ??
        item.grupoNome ??
        item.ClienteOcultoGrupoNome ??
        item.clienteOcultoGrupoNome ??
        "";
      let perguntaTitulo = perguntaTituloBase
        ? perguntaTituloBase
        : ehClienteOculto && perguntaId
        ? `Cliente oculto - ${perguntaId}`
        : perguntaTituloBase;

      perguntaTitulo = extractCriterionDescription(perguntaTitulo || "");

      const rawStatus = item.Status ?? item.status ?? "";
      const status = normalizeReviewStatus(rawStatus);

      return {
        seloRevisaoTecnicaId:
          item.SeloRevisaoTecnicaId ?? item.seloRevisaoTecnicaId,
        municipioId: item.MunicipioId ?? item.municipioId,
        perguntaId: item.PerguntaId ?? item.perguntaId,
        perguntaTitulo,
        grupoNome: grupoNomeBase,
        notaOriginal: item.NotaOriginal ?? item.notaOriginal,
        notaPleiteada: item.NotaPleiteada ?? item.notaPleiteada,
        notaHomologada: item.NotaHomologada ?? item.notaHomologada ?? null,
        status,
        rawStatus,
        dataCriacao: item.DataCriacao ?? item.dataCriacao,
        dataAtualizacao: item.DataAtualizacao ?? item.dataAtualizacao,
        dataEnvio: item.DataEnvio ?? item.dataEnvio,
        dataConclusao: item.DataConclusao ?? item.dataConclusao,
        responsavelNome: item.ResponsavelNome ?? item.responsavelNome,
        ehPerguntaClienteOculto: ehClienteOculto,
        ehRevisaoNacional,
      };
    },
    formatDate(value) {
      if (!value) return "—";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      return date.toLocaleDateString("pt-BR");
    },
    detailRoute(id) {
      if (!id) {
        return "";
      }
      return `/cidade/${this.municipioId}/revisoes-tecnicas/${id}`;
    },
    editRoute(id) {
      if (!id) {
        return "";
      }
      return `/cidade/${this.municipioId}/revisoes-tecnicas/${id}/editar`;
    },
    canEdit(item) {
      const statusMeta = findStatusMeta(item.status);
      return (
        this.canManage &&
        ["RASCUNHO", "COMPLEMENTAR"].includes(statusMeta.value) &&
        (statusMeta.value !== "RASCUNHO" || this.canViewDrafts)
      );
    },
  },
};
</script>

<style scoped>
#technical-review-list {
  min-height: 80vh;
}
</style>

